import {
    createContext,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type PropsWithChildren,
} from 'react'

import { useI18n } from '@muse/i18n'
import { useNavigate } from '@muse-web/router'
import { toast } from '@ds/components'

import { sendEvent } from '@core/analytics'
import { getAuthorPageUrl } from '@core/routes'
import { useCurrentUser } from '@features/session'

import { getAudioUploadPageUrl } from '@features/audio'
import { getBatchLosslessFormat, useHqStoragePaywall } from '@features/paywall'
import { UploadFileInput } from '../components/upload-file-input'
import { setUploadingFileIds, waitForUploads } from '../helpers/uploadQueueWaiters'
import { AsyncUploadSettingsDialogContainer } from '../containers/AsyncUploadSettingsDialogContainer'
import { AsyncUnsupportedFileDialogContainer } from '../containers/AsyncUnsupportedFileDialogContainer'
import { preloadUploadAudioFile } from '../services/uploadAudioFileAsync'
import {
    useAudioUploadFlow,
    type TQueuedFile,
    type TUploadedAudio,
    type TUploadFlowError,
    type TUploadItem,
    type TUploadSettings,
} from '../hooks/useAudioUploadFlow'

/** Reported as `upload_click.placement`. */
export type TUploadPlacement = 'header' | 'audio' | 'not_found'

type TUploadFlowValue = {
    /** Must be called straight from a click handler, or the browser blocks the picker. */
    openFilePicker: (placement: TUploadPlacement) => void
    selectFiles: (files: File[], placement: TUploadPlacement) => void
    /** Starts a batch nextjs staged before it handed the document over. */
    adoptBatch: (queued: TQueuedFile[], settings: TUploadSettings, batchId: string) => void
    items: TUploadItem[]
    displayedItems: TUploadItem[]
    isUploading: boolean
    clear: () => void
    /** Drops one file from the batch — the per-card abort on the profile. */
    cancelItem: (fileId: string) => void
    cancel: () => void
}

const NOOP_VALUE: TUploadFlowValue = {
    openFilePicker: () => undefined,
    selectFiles: () => undefined,
    adoptBatch: () => undefined,
    items: [],
    displayedItems: [],
    isUploading: false,
    clear: () => undefined,
    cancelItem: () => undefined,
    cancel: () => undefined,
}

export const UploadFlowContext = createContext<TUploadFlowValue>(NOOP_VALUE)

/**
 * Mounted above the router, in the app layout: the picked files, their progress and the abort
 * controller have to survive both the soft navigation to the review screen and the mobile drawer
 * unmounting itself mid-pick.
 */
export const UploadFlowProvider = ({ children }: PropsWithChildren) => {
    const { t } = useI18n()
    const navigate = useNavigate()
    const { username } = useCurrentUser()
    const inputRef = useRef<HTMLInputElement>(null)
    const placementRef = useRef<TUploadPlacement>('header')
    const [isUnsupportedFileOpen, setIsUnsupportedFileOpen] = useState(false)
    // The batch handlers are passed into the flow, so this is the only way back to it.
    const clearRef = useRef<() => void>(() => undefined)
    const hqStoragePaywall = useHqStoragePaywall({
        isFirstUpload: browsableAudioCount => browsableAudioCount === 0,
    })

    // Written out because `t` keys are typed literals — a computed key won't compile.
    const errorMessages: Record<Exclude<TUploadFlowError, 'unsupported-file'>, string> = {
        'invalid-format': t('i18n.uploadErrorInvalidFormat'),
        'too-large': t('i18n.uploadErrorTooLarge'),
        'upload-failed': t('i18n.uploadErrorFailed'),
        'upload-in-progress': t('i18n.uploadErrorInProgress'),
        'handover-failed': t('i18n.uploadErrorProcessing'),
    }

    const handleError = (reason: TUploadFlowError, fileName: string) => {
        if (reason === 'unsupported-file') {
            setIsUnsupportedFileOpen(true)

            return
        }

        // Not every reason is about one file.
        toast.error(fileName ? `${fileName}: ${errorMessages[reason]}` : errorMessages[reason])
    }

    // A single file goes to its review screen; a batch goes to the profile and reports progress
    // there, as audio-web does — there is no batch screen.
    const handleBatchStarted = (fileIds: string[]) => {
        const [firstFileId] = fileIds

        navigate(
            fileIds.length === 1
                ? getAudioUploadPageUrl({ authorUsername: username, fileId: firstFileId })
                : getAuthorPageUrl({ authorUsername: username }),
        )
    }

    const handleBatchFilesStarted = (queued: TQueuedFile[]) => {
        const losslessFormat = getBatchLosslessFormat(queued.map(({ file }) => file))

        if (!losslessFormat) {
            return
        }

        const fileIds = queued.map(({ fileId }) => fileId)

        hqStoragePaywall.showWhenReady(losslessFormat, {
            onBeforeCheckout: signal => waitForUploads(fileIds, signal),
        })
    }

    const handleBatchFinished = (uploaded: TUploadedAudio[], queuedCount: number) => {
        // A lone successful upload stays: the review form is its next step and reads its item.
        if (queuedCount === 1 && uploaded.length === 1) {
            return
        }

        // Everything else already sits on the profile, or is a failure with nothing to review —
        // drop the items either way, or they linger as phantom uploads for the whole session.
        clearRef.current()

        if (queuedCount === 1) {
            navigate(getAuthorPageUrl({ authorUsername: username }))
        }
    }

    const flow = useAudioUploadFlow({
        onError: handleError,
        onBatchStarted: handleBatchStarted,
        onBatchFilesStarted: handleBatchFilesStarted,
        onBatchFinished: handleBatchFinished,
    })

    // A ref so the exposed callbacks stay stable while still calling the current flow.
    const selectFilesRef = useRef(flow.selectFiles)
    const adoptBatchRef = useRef(flow.adoptBatch)

    selectFilesRef.current = flow.selectFiles
    adoptBatchRef.current = flow.adoptBatch
    clearRef.current = flow.clear

    useEffect(() => {
        setUploadingFileIds(
            flow.items.filter(item => item.status === 'uploading').map(item => item.fileId),
        )
    }, [flow.items])

    const closeUnsupportedFile = () => {
        setIsUnsupportedFileOpen(false)
    }

    const handlePickerOpen = () => {
        sendEvent({ name: 'upload_click', payload: { placement: placementRef.current } })
    }

    // Stable, so a progress tick doesn't rebuild the context value.
    const openFilePicker = useCallback((placement: TUploadPlacement) => {
        placementRef.current = placement
        inputRef.current?.click()
        preloadUploadAudioFile()
    }, [])

    // Drag & drop never opens the picker, so the click that usually reports the upload never fires.
    const selectFiles = useCallback((files: File[], placement: TUploadPlacement) => {
        sendEvent({ name: 'upload_click', payload: { placement } })
        preloadUploadAudioFile()
        selectFilesRef.current(files)
    }, [])

    const adoptBatch = useCallback(
        (queued: TQueuedFile[], settings: TUploadSettings, batchId: string) => {
            preloadUploadAudioFile()
            adoptBatchRef.current(queued, settings, batchId)
        },
        [],
    )

    const value = useMemo<TUploadFlowValue>(
        () => ({
            openFilePicker,
            selectFiles,
            adoptBatch,
            items: flow.items,
            displayedItems: flow.displayedItems,
            isUploading: flow.isUploading,
            clear: flow.clear,
            cancelItem: flow.cancelItem,
            cancel: flow.cancel,
        }),
        [
            openFilePicker,
            selectFiles,
            adoptBatch,
            flow.items,
            flow.displayedItems,
            flow.isUploading,
            flow.clear,
            flow.cancelItem,
            flow.cancel,
        ],
    )

    return (
        <UploadFlowContext.Provider value={value}>
            {children}
            <UploadFileInput
                ref={inputRef}
                onSelectFiles={flow.selectFiles}
                onPickerOpen={handlePickerOpen}
                testID="upload-file-input"
            />
            {flow.isSettingsOpen ? (
                <AsyncUploadSettingsDialogContainer
                    open
                    onSubmit={flow.submitSettings}
                    onCancel={flow.cancelSettings}
                />
            ) : null}
            {isUnsupportedFileOpen ? (
                <AsyncUnsupportedFileDialogContainer onClose={closeUnsupportedFile} />
            ) : null}
        </UploadFlowContext.Provider>
    )
}
